import React, { useRef } from "react"
import { Grid, TextField, Button, Typography } from "@material-ui/core"
import { Autocomplete } from "@material-ui/lab"
import { Add as AddIcon } from "@material-ui/icons"

import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"
import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"
import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"
import DeleteButton from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/DeleteButton"

import { formatSaveInputLocationOptions, SaveInputLocationData, superlogicaFieldOptions } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"

import { ChatBotFlowBlockContent } from "@/protocols/chatBot"
import { SuperlogicaFieldOption } from "@/protocols/SuperlogicaPluginProtocol"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/SuperlogicaLookupContactBlock/ResponseMappingSectionEditor/styles"

import { colors } from "@/styles/colors"

type ResponseMappingSectionEditorProps = {
	onSave: (chatBotFlowBlockContent: ChatBotFlowBlockContent) => void
	chatBotFlowBlockContent: ChatBotFlowBlockContent
}

const ResponseMappingSectionEditor: React.FC<ResponseMappingSectionEditorProps> = ({
	chatBotFlowBlockContent,
	onSave,
	children
}) => {
	const contentEditor = useContentEditor({ chatBotFlowBlockContent })
	const superlogicaBlock = contentEditor.content?.superlogicaBlock
	const responseMapping = superlogicaBlock?.responseMapping || []
	const classes = useStyles()
	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()
	const scrollContainerRef = useRef<HTMLDivElement>(null)

	const customFieldOptions = formatSaveInputLocationOptions(chatBotFlowConstructorStore.constructionResources)
		.filter(option => option.type === "custom-field")

	const handleChangeSuperlogicaField = (index: number, selected: SuperlogicaFieldOption | null) => {
		if (!selected) {
			return
		}

		const prev = responseMapping[index]
		const updatedMapping = {
			...prev,
			field: selected.value
		}

		const updated = [...responseMapping]
		updated[index] = updatedMapping

		contentEditor.changeContent({
			superlogicaBlock: {
				...superlogicaBlock,
				responseMapping: updated
			}
		})
	}

	const handleChangeCustomField = (index: number, selected: SaveInputLocationData | null) => {
		const prev = responseMapping[index]
		const updatedMapping = {
			...prev,
			customFieldId: selected?.customFieldId
		}

		const updated = [...responseMapping]
		updated[index] = updatedMapping

		contentEditor.changeContent({
			superlogicaBlock: {
				...superlogicaBlock,
				responseMapping: updated
			}
		})
	}

	const handleRemoveMapping = (index: number) => {
		const updatedMapping = [...responseMapping]
		updatedMapping.splice(index, 1)

		contentEditor.changeContent({
			superlogicaBlock: {
				...superlogicaBlock,
				responseMapping: updatedMapping
			}
		})
	}

	const handleAddMapping = () => {
		const newMapping = [
			...responseMapping,
			{
				field: superlogicaFieldOptions[0].value,
				customFieldId: undefined
			}
		]

		contentEditor.changeContent({
			superlogicaBlock: {
				...superlogicaBlock,
				responseMapping: newMapping
			}
		})

		setTimeout(() => {
			scrollContainerRef.current?.scrollIntoView({
				block: "end",
				behavior: "smooth"
			})
		}, 100)
	}

	const handleOpen = () => contentEditor.reset()
	const handleSave = () => onSave(contentEditor.content)

	return (
		<BlockBodySectionEditor
			title="Mapear resposta"
			onSave={handleSave}
			onOpen={handleOpen}
			AsideDrawerProps={{
				children: (
					<Grid container direction="column" spacing={2}>
						<Grid item xs={12}>
							<Grid container direction="column" spacing={3}>
								<Grid item xs={12} ref={scrollContainerRef}>
									<Grid container direction="column" style={{ gap: 16 }}>
										{responseMapping.map((mapping, index) => {
											const selectedSuperlogicaField = superlogicaFieldOptions.find(option => option.value === mapping.field) || null
											const selectedCustomField = customFieldOptions.find(option => option.customFieldId === mapping.customFieldId) || null

											return (
												<Grid item xs={12} key={index}>
													<Grid container direction="column" style={{ gap: 8 }}>
														<Grid item xs={12} className={classes.associationRow}>
															<Typography component="span" className={classes.associationSentence}>
																Salvar o(a){" "}
																<Autocomplete
																	value={selectedSuperlogicaField}
																	options={superlogicaFieldOptions}
																	getOptionSelected={(option, value) => option?.value === value?.value}
																	getOptionLabel={(option) => option?.label || "Nenhum dado selecionado"}
																	onChange={(_, selected) => handleChangeSuperlogicaField(index, selected)}
																	classes={{ root: classes.inlineAutocompleteRoot }}
																	renderInput={(params) => (
																		<TextField
																			{...params}
																			className={classes.inlineSelectInput}
																			variant="outlined"
																			placeholder="Nenhum dado selecionado"
																			margin="none"
																		/>
																	)}
																	size="small"
																	disableClearable
																/>
																{" "}do contato no campo personalizado{" "}
																<Autocomplete
																	value={selectedCustomField}
																	options={customFieldOptions}
																	getOptionSelected={(option, value) => option?.customFieldId === value?.customFieldId}
																	getOptionLabel={(option) => option?.name || "Nenhum campo selecionado"}
																	onChange={(_, selected) => handleChangeCustomField(index, selected)}
																	onOpen={async () => {
																		await chatBotFlowConstructorStore.loadSpecificConstructionResources("saveInputLocationOptions")
																	}}
																	classes={{ root: classes.inlineAutocompleteRoot }}
																	renderInput={(params) => (
																		<TextField
																			{...params}
																			className={classes.inlineSelectInput}
																			variant="outlined"
																			placeholder="Nenhum campo selecionado"
																			margin="none"
																		/>
																	)}
																	size="small"
																	disableClearable
																/>
																.
															</Typography>
														</Grid>
														<Grid item container justifyContent="flex-end">
															<Grid item>
																<DeleteButton
																	onClick={() => handleRemoveMapping(index)}
																/>
															</Grid>
														</Grid>
													</Grid>
												</Grid>
											)
										})}
									</Grid>
								</Grid>
								<Grid item xs={12} container justifyContent="flex-end">
									<Grid item>
										<Button
											className={classes.addAssociationButton}
											onClick={handleAddMapping}
											startIcon={<AddIcon style={{ color: colors.blue[700] }} />}
										>
											Adicionar associação
										</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				)
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default ResponseMappingSectionEditor
